// @refresh reset
import { type FC } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "~/shared/ui/button";
import { Input } from "~/shared/ui/input";
import { Select, SelectContent, SelectItem, SelectValue } from "~/shared/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/shared/ui/tooltip";
import { cn } from "~/shared/lib/utils";
import { FILTER_FIELD_CLASS, FilterSelectTrigger } from "./filter-field-primitives";
import { ComboboxFilter } from "./combobox-filter";
import { DateRangeFilter } from "./date-range-filter";
import { EmailDomainFilter } from "./email-domain-filter";
import { NumberRangeFilter } from "./number-range-filter";

// Re-exported for backwards compatibility: value parsers now live alongside
// their respective filter components, but consumers import them from here.
export { parseDateRangeValue } from "./date-range-from-preset";
export { parseEmailDomainValue } from "./email-domain-filter";
export { parseNumberRangeValue } from "./number-range-filter";

export interface FilterFieldConfig {
    key: string;
    label: string;
    type: "select" | "date" | "text" | "combobox" | "daterange" | "email_domain" | "number_range";
    options?: { value: string; label: string; className?: string }[];
    placeholder?: string;
    maxWidth?: string;
    allowCustomValue?: boolean;
    max?: string;
    disabled?: boolean;
    /** Hover tooltip explaining WHY the field is disabled (e.g. "select a project first").
     *  Shown only when `disabled` is true; the field stays dimmed but hoverable. */
    disabledHint?: string;
    /** For daterange: preset options labels */
    daterangeLabels?: {
        custom?: string;
        from?: string;
        to?: string;
    };
    /** For email_domain: mode and placeholder labels */
    emailDomainLabels?: {
        include?: string;
        exclude?: string;
        placeholder?: string;
    };
    /** For number_range: min and max input labels */
    numberRangeLabels?: {
        min?: string;
        max?: string;
    };
}

export const DataTableFilterButton: FC<{
    isOpen: boolean;
    onToggle: () => void;
    label?: string;
}> = ({ isOpen, onToggle, label = "Filter" }) => (
    <Button variant="outline" size="sm" className="h-11 gap-1.5 md:h-9" onClick={onToggle}>
        {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        {label}
    </Button>
);

interface DataTableFilterPanelProps {
    fields: FilterFieldConfig[];
    values: Record<string, unknown>;
    onChange: (key: string, value: unknown) => void;
    onApply: () => void;
    onReset: () => void;
    isOpen: boolean;
    applyLabel?: string;
    resetLabel?: string;
    customValueHint?: string;
    noResultsText?: string;
}

export const DataTableFilterPanel: FC<DataTableFilterPanelProps> = ({
    fields,
    values,
    onChange,
    onApply,
    onReset,
    isOpen,
    applyLabel = "APPLY FILTER",
    resetLabel = "RESET FILTER",
    customValueHint,
    noResultsText,
}) => {
    return (
        <div
            className={cn(
                "transition-all duration-200",
                // Mobile stacks the fields into a tall column, so the open-state cap must be
                // generous enough to fit them PLUS the action row — otherwise the bottom button
                // (Apply) overflows past the cap and is covered by the content below. Desktop keeps
                // the proven 500px cap (fields sit in a row there).
                isOpen
                    ? "max-h-[1000px] overflow-visible opacity-100 md:max-h-[500px]"
                    : "max-h-0 overflow-hidden opacity-0",
            )}
        >
            <div className="rounded-[10px] border border-border p-4">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-start gap-4">
                        {fields.map((field) => {
                            const val = values[field.key];
                            const hasValue = val !== undefined && val !== "" && val !== "__all__";
                            const control = (
                                <div className={cn("relative", field.disabled && "pointer-events-none")}>
                                    {field.type === "text" && (
                                        <Input
                                            value={(values[field.key] as string) ?? ""}
                                            onChange={(e) => onChange(field.key, e.target.value || undefined)}
                                            placeholder={field.placeholder}
                                            className={cn(
                                                "h-9 !px-3",
                                                FILTER_FIELD_CLASS,
                                                hasValue && "!pr-7",
                                            )}
                                        />
                                    )}
                                    {field.type === "select" && field.options && (
                                        <Select
                                            value={(values[field.key] as string) ?? "__all__"}
                                            onValueChange={(v) => {
                                                onChange(field.key, v === "__all__" ? undefined : v);
                                            }}
                                        >
                                            <FilterSelectTrigger
                                                className={cn(
                                                    "h-9 !w-full focus-visible:border-input focus-visible:ring-0",
                                                    FILTER_FIELD_CLASS,
                                                    // Strip pill styling (rounded bg / padding) from the
                                                    // selected SelectItem when rendered inside the trigger,
                                                    // keep only the coloured text. Pill stays in dropdown.
                                                    "[&_span[data-pill]]:!rounded-none [&_span[data-pill]]:!bg-transparent [&_span[data-pill]]:!px-0 [&_span[data-pill]]:!py-0",
                                                    hasValue && "pr-7",
                                                )}
                                            >
                                                <SelectValue placeholder={field.placeholder} />
                                            </FilterSelectTrigger>
                                            <SelectContent
                                                position="popper"
                                                align="start"
                                                side="bottom"
                                                avoidCollisions={false}
                                            >
                                                {field.options.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.className ? (
                                                            <span
                                                                data-pill
                                                                className={cn(
                                                                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                                    opt.className,
                                                                )}
                                                            >
                                                                {opt.label}
                                                            </span>
                                                        ) : (
                                                            opt.label
                                                        )}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                    {field.type === "combobox" && field.options && (
                                        <ComboboxFilter
                                            options={field.options}
                                            value={(values[field.key] as string) ?? "__all__"}
                                            onChange={(v) =>
                                                onChange(field.key, v === "__all__" ? undefined : v)
                                            }
                                            placeholder={field.placeholder}
                                            allowCustomValue={field.allowCustomValue}
                                            customValueHint={customValueHint}
                                            noResultsText={noResultsText}
                                            hasValue={hasValue}
                                        />
                                    )}
                                    {field.type === "daterange" && (
                                        <DateRangeFilter
                                            value={(values[field.key] as string) ?? ""}
                                            onChange={(v) => onChange(field.key, v)}
                                            labels={field.daterangeLabels}
                                            hasValue={hasValue}
                                        />
                                    )}
                                    {field.type === "email_domain" && (
                                        <EmailDomainFilter
                                            value={(values[field.key] as string) ?? ""}
                                            onChange={(v) => onChange(field.key, v)}
                                            labels={field.emailDomainLabels}
                                            hasValue={hasValue}
                                        />
                                    )}
                                    {field.type === "number_range" && (
                                        <NumberRangeFilter
                                            value={(values[field.key] as string) ?? ""}
                                            onChange={(v) => onChange(field.key, v)}
                                            labels={field.numberRangeLabels}
                                        />
                                    )}
                                    {field.type === "date" && (
                                        <Input
                                            type="date"
                                            value={(values[field.key] as string) ?? ""}
                                            onChange={(e) => onChange(field.key, e.target.value || undefined)}
                                            max={field.max}
                                            className={cn(
                                                "h-9 !px-3",
                                                FILTER_FIELD_CLASS,
                                                hasValue && "!pr-7",
                                            )}
                                        />
                                    )}
                                    {hasValue &&
                                        field.type !== "daterange" &&
                                        field.type !== "email_domain" &&
                                        field.type !== "number_range" && (
                                            <button
                                                type="button"
                                                className="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-sm bg-inherit p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                                                onClick={() => onChange(field.key, undefined)}
                                            >
                                                <X className="size-3.5" />
                                            </button>
                                        )}
                                </div>
                            );
                            return (
                                <div
                                    key={field.key}
                                    className={cn("flex flex-col gap-1", field.disabled && "opacity-60")}
                                    style={{
                                        width: field.maxWidth,
                                        minWidth: field.maxWidth ?? "260px",
                                    }}
                                >
                                    <label className="text-sm text-muted-foreground">{field.label}</label>
                                    {field.disabled && field.disabledHint ? (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="cursor-not-allowed">{control}</div>
                                            </TooltipTrigger>
                                            <TooltipContent>{field.disabledHint}</TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        control
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {/* Action row below the fields: stacked full-width on mobile (Reset on top,
                        Apply at the bottom), a right-aligned horizontal row from md up. */}
                    <div className="flex flex-col gap-2 md:flex-row md:justify-end">
                        <Button variant="outline" size="sm" onClick={onReset} className="w-full md:w-auto">
                            {resetLabel}
                        </Button>
                        <Button size="sm" onClick={onApply} className="w-full md:w-auto">
                            {applyLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
